"use client";

import type { BabyNameEntry } from "@/lib/api/baby-names";
import Badge from "@ishvaram/ui/Badge";
import { ScoreBadge } from "@ishvaram/ui/ScoreBadge";
import { BABY_NAMES_COPY, type BabyNamesCopy } from "./copy";
import { nameSyllableHi } from "./syllables";

interface NameCardProps {
  entry: BabyNameEntry;
  selected: boolean;
  onToggle: () => void;
  copy?: BabyNamesCopy;
  /** Hindi page: Devanagari name and syllable lead, the romanised name is the gloss. */
  hindi?: boolean;
}

export function NameCard({
  entry,
  selected,
  onToggle,
  copy = BABY_NAMES_COPY.en,
  hindi = false,
}: NameCardProps) {
  const [title, gloss] =
    hindi && entry.name_hi ? [entry.name_hi, entry.name] : [entry.name, entry.name_hi];
  const letter = hindi
    ? nameSyllableHi(entry.nakshatra, entry.starting_letter, entry.name_hi)
    : entry.starting_letter;
  const genderVariant =
    entry.gender === "male" ? "accent" : entry.gender === "female" ? "error" : "accent";

  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={copy.toggle(entry.name, selected)}
      className={`card-sacred w-full cursor-pointer border-2 p-4 text-left transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-intent-brand focus-visible:ring-offset-2 ${
        selected ? "border-border-interactive bg-surface-brand-subtle" : "border-transparent"
      }`}
      onClick={onToggle}
      data-design-owner="baby-name-card"
    >
      <span className="flex items-start justify-between gap-3">
        <span className="min-w-0 flex-1">
          <span className="mb-1 flex items-center gap-2">
            <span
              role="heading"
              aria-level={3}
              className="truncate font-serif text-body font-bold text-text-primary"
            >
              {title}
            </span>
            <Badge
              label={copy.genderBadges[entry.gender] ?? entry.gender}
              variant={genderVariant}
              className="capitalize"
            />
          </span>
          <span className="mb-1 block text-body-small text-text-secondary">{gloss}</span>
          <span
            lang={hindi ? "en" : undefined}
            className="block text-body-small leading-snug text-text-secondary"
          >
            {entry.meaning}
          </span>
          <span className="mt-2 flex items-center gap-3 text-caption font-medium text-text-muted">
            <span>
              {copy.letter} {letter}
            </span>
            <span>
              {copy.nameNumber} {entry.name_number}
            </span>
            <span>{copy.origins[entry.origin] ?? entry.origin}</span>
          </span>
        </span>
        <span className="flex flex-shrink-0 flex-col items-center">
          <ScoreBadge score={entry.compatibility_score} max={10} size="lg" />
          <span className="mt-1 text-caption font-bold uppercase tracking-eyebrow text-text-muted">
            {copy.rating(entry.compatibility_score)}
          </span>
        </span>
      </span>
      {selected && (
        <span className="mt-2 block rounded bg-surface-brand-subtle py-1 text-center text-caption font-bold text-intent-brand">
          {copy.added}
        </span>
      )}
    </button>
  );
}
